import React from 'react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'

/**
 * Raw HTML is kept for formatting, but nothing in it may run code, submit data or load anything
 * when the message is shown: everything rendered here is bot / LLM output, which can be steered
 * by text the model read (comments, files, web pages), and a load on render would send data to
 * any host without a click. So elements that do that are dropped (below), every element keeps
 * only a short list of harmless attributes (rehypeKeepSafeAttributes), and images become links
 * (ImageAsLink).
 */
const DISALLOWED_ELEMENTS = [
  'script',
  'style',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'portal',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'svg',
  'math',
  'video',
  'audio',
  'source',
  'track',
  'canvas',
  'template',
  'noscript',
]

/**
 * The only attributes raw HTML keeps (hast property names), per tag. Everything else is removed:
 * attributes that load something (style, src, srcset, background, poster, ping...) and classes
 * and ids, which could pull in the app's own styles (e.g. a full-screen panel over the app).
 * `src` stays on <img> only, which ImageAsLink turns into a link.
 */
const ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  '*': ['title'],
  a: ['href'],
  img: ['src', 'alt'],
  ol: ['start'],
  td: ['colSpan', 'rowSpan', 'align'],
  th: ['colSpan', 'rowSpan', 'align'],
}

type HastNode = {
  type?: string
  tagName?: string
  properties?: Record<string, unknown>
  children?: HastNode[]
}

const keepSafeAttributes = (node: HastNode) => {
  if (node.type === 'element' && node.properties) {
    const { properties } = node
    const allowed = [...ALLOWED_ATTRIBUTES['*'], ...(ALLOWED_ATTRIBUTES[node.tagName ?? ''] ?? [])]
    Object.keys(properties).forEach((name) => {
      if (!allowed.includes(name)) delete properties[name]
    })
  }
  node.children?.forEach(keepSafeAttributes)
}

/** Rehype plugin (after rehype-raw): keeps only ALLOWED_ATTRIBUTES on every element. */
export const rehypeKeepSafeAttributes = () => (tree: HastNode) => {
  keepSafeAttributes(tree)
}

/**
 * The image address when a link to it is safe: http(s) or relative. Anything else (javascript:,
 * data:, vbscript:...) gives undefined. react-markdown filters link hrefs this way, but not image
 * sources, and ImageAsLink turns an image source into a link.
 */
export const safeImageHref = (src?: string): string | undefined => {
  const url = (src || '').trim()
  if (!url) return undefined
  if (url.startsWith('/') || url.startsWith('#')) return url
  const colon = url.indexOf(':')
  if (colon === -1) return url
  const protocol = url.slice(0, colon).toLowerCase()
  if (protocol === 'http' || protocol === 'https') return url
  // A colon after "?" or "#" belongs to the query or fragment of a relative address.
  const query = url.indexOf('?')
  const hash = url.indexOf('#')
  if ((query !== -1 && colon > query) || (hash !== -1 && colon > hash)) return url
  return undefined
}

/** Images are shown as links: loading them on render would let a reply send data to any host. */
const ImageAsLink = ({ src, alt }: { src?: string; alt?: string }) => {
  const href = safeImageHref(src)
  if (!href) return alt ? <span>{alt}</span> : null
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {alt || href}
    </a>
  )
}

/**
 * Shows the text as it is when Markdown fails while rendering it (a throw there is not caught by
 * the try below), instead of unmounting the whole page with it. Tries again when the text changes
 * (a reply still being typed).
 */
class PlainTextOnError extends React.Component<
  { text: string; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown) {
    console.warn('Markdown rendering error:', error)
  }

  componentDidUpdate(previous: { text: string }) {
    if (this.state.failed && previous.text !== this.props.text) this.setState({ failed: false })
  }

  render() {
    if (this.state.failed) return <div style={{ whiteSpace: 'pre-wrap' }}>{this.props.text}</div>
    return this.props.children
  }
}

interface SafeMarkdownProps {
  children: string
  className?: string
  components?: any
}

/**
 * Safe Markdown wrapper component that handles errors gracefully
 * Provides consistent markdown rendering with enhanced table support and fallback formatting
 */
export const SafeMarkdown: React.FC<SafeMarkdownProps> = ({
  children,
  className = '',
  components = {},
}) => {
  try {
    // No remark plugins: without remark-gfm, pipes never make tables (so they need no escaping,
    // which showed a literal "&#124;" in code); and nothing renders math, so remark-math only lost
    // dollar signs ("$7.25 an hour … $15,080 a year", "$$ or $$$") and crashed on a line starting
    // with "$$" (remark-math 6 does not work with react-markdown 8).
    const cleanContent = children || ''

    return (
      <div className={className}>
        <PlainTextOnError text={cleanContent}>
          <Markdown
            rehypePlugins={[rehypeRaw, rehypeKeepSafeAttributes] as any}
            disallowedElements={DISALLOWED_ELEMENTS}
            components={{
              // Default components for basic formatting
              p: ({ children }) => <p className="mb-2">{children}</p>,
              ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
              li: ({ children }) => <li className="mb-1">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              em: ({ children }) => <em className="italic">{children}</em>,
              code: ({ children }) => <span>{children}</span>,
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-gray-300 pl-4 italic">
                  {children}
                </blockquote>
              ),
              h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
              h2: ({ children }) => <h2 className="text-xl font-bold mb-3">{children}</h2>,
              h3: ({ children }) => <h3 className="text-lg font-bold mb-2">{children}</h3>,
              // Handle tables manually with simple rendering
              table: ({ children }) => (
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full border-collapse border border-gray-300">
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
              tbody: ({ children }) => <tbody>{children}</tbody>,
              tr: ({ children }) => <tr className="border-b border-gray-200">{children}</tr>,
              td: ({ children }) => (
                <td className="px-3 py-2 border border-gray-300">{children}</td>
              ),
              th: ({ children }) => (
                <th className="px-3 py-2 border border-gray-300 font-semibold text-left">
                  {children}
                </th>
              ),
              // Allow custom components to override defaults
              ...components,
              // Never overridden: an image renderer could load images again.
              img: ImageAsLink,
            }}
          >
            {cleanContent}
          </Markdown>
        </PlainTextOnError>
      </div>
    )
  } catch (error) {
    console.warn('Markdown parsing error:', error)
    // Enhanced fallback with basic HTML-like formatting
    const fallbackContent = children
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br/>')

    return (
      <div
        className={className}
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: fallbackContent }}
      />
    )
  }
}

export default SafeMarkdown
